import React, { useEffect, useRef } from "react";
import { Grid, Tabs } from "@sumprio/ui";
import { MessageTextIcon } from "@sumprio/ui/icons";
import { EmptyState, NotesFeedPanel } from "components/common";
import type { FeedItem, NotesFeedPanelProps } from "components/common";

export type TabbedMessagesPanelTabId = "messages" | "attachment";

export interface TabbedMessagesPanelProps {
  activeTab: TabbedMessagesPanelTabId;
  onTabChange: (_tab: TabbedMessagesPanelTabId) => void;
  onAttachmentTabActive?: () => void;

  messages: FeedItem[];
  messageInput: Pick<
    NotesFeedPanelProps,
    | "showInput"
    | "inputValue"
    | "onInputChange"
    | "onPost"
    | "posting"
    | "inputPlaceholder"
    | "sendButtonLabel"
    | "showAttachmentIcon"
    | "inputAutoFocus"
  >;

  attachmentsTab?: {
    title?: string;
    icon?: React.ComponentType<any>;
    content: React.ReactNode;
  };

  tabsMarginBottom?: number;
}

export default function TabbedMessagesPanel({
  activeTab,
  onTabChange,
  onAttachmentTabActive,
  messages,
  messageInput,
  attachmentsTab,
  tabsMarginBottom = 24,
}: TabbedMessagesPanelProps) {
  const onAttachmentTabActiveRef = useRef(onAttachmentTabActive);
  onAttachmentTabActiveRef.current = onAttachmentTabActive;

  useEffect(() => {
    if (activeTab === "attachment") onAttachmentTabActiveRef.current?.();
  }, [activeTab]);

  const showAttachments = !!attachmentsTab;
  const tabs = [
    { id: "messages", icon: MessageTextIcon, title: "Messages" },
    ...(showAttachments
      ? [
          {
            id: "attachment",
            icon: attachmentsTab?.icon,
            title: attachmentsTab?.title ?? "Attachment",
          },
        ]
      : []),
  ] as const;

  return (
    <Grid
      className="tabbed-messages-panel"
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      style={{ minHeight: 0 }}
    >
      {tabs.length > 1 ? (
        <Tabs
          items={tabs as unknown as any}
          selectedTabId={activeTab}
          onChange={(tabId) => onTabChange(tabId as TabbedMessagesPanelTabId)}
          size="sm"
          style={{ marginBottom: tabsMarginBottom }}
        />
      ) : null}

      {activeTab === "messages" && (
        <div className="tabbed-messages-panel__body">
          <NotesFeedPanel
            items={messages}
            emptyTitle="No messages yet"
            emptyDescription="Start the conversation to collaborate on this request, share updates, or ask for more details."
            emptyIcon={<MessageTextIcon size="xl" />}
            showInput={messageInput.showInput ?? true}
            inputValue={messageInput.inputValue ?? ""}
            onInputChange={messageInput.onInputChange}
            onPost={messageInput.onPost}
            posting={messageInput.posting ?? false}
            inputPlaceholder={messageInput.inputPlaceholder ?? "Leave your note here..."}
            sendButtonLabel={messageInput.sendButtonLabel ?? "Post"}
            showAttachmentIcon={messageInput.showAttachmentIcon ?? false}
            inputAutoFocus={messageInput.inputAutoFocus ?? false}
          />
        </div>
      )}

      {activeTab === "attachment" && (
        <Grid flex={1} display="flex" flexDirection="column" width="100%" style={{ minWidth: 0 }}>
          {showAttachments ? (
            attachmentsTab?.content
          ) : (
            <EmptyState title="No attachment" description="No file has been attached." />
          )}
        </Grid>
      )}
    </Grid>
  );
}
